import { cloneDeep } from 'lodash-es';
import { HttpParams } from '@angular/common/http';
import * as i0 from '@angular/core';
import { Injectable, LOCALE_ID, Inject } from '@angular/core';
import * as i1 from '@polaris-customerweb/sharedcomponents.config';
import * as i2 from '@polaris-customerweb/sharedcomponents.core';
import { CacheCollectionObservable } from '@polaris-customerweb/sharedcomponents.core';
import * as i3 from '@polaris-customerweb/sharedcomponents.auth';
import { BehaviorSubject, switchMap, of, map, share, firstValueFrom, catchError, tap } from 'rxjs';

class UserProfile {
    constructor(contract) {
        this._notifyEvents = null;
        this._notifyRecommendedMaintenance = null;
        this._notifyServiceBulletin = null;
        this._settings = null;
        this.contract = contract;
    }
    get acceptedEulas() {
        return this.contract.acceptedEulas ?? [];
    }
    set acceptedEulas(acceptedEulas) {
        this.contract.acceptedEulas = [...acceptedEulas];
    }
    get country() {
        return this.contract.country || '';
    }
    set country(country) {
        this.contract.country = country;
    }
    get currentRideType() {
        return this.contract.currentRideType ?? 'None';
    }
    set currentRideType(rideType) {
        this.contract.currentRideType = rideType;
    }
    get email() {
        return this.contract.email || '';
    }
    get eulaVersion() {
        return this.contract.eulaVersion ?? '';
    }
    get favoriteDealers() {
        return this.contract.favoriteDealers ? Object.freeze([...this.contract.favoriteDealers]) : [];
    }
    set favoriteDealers(dealerIds) {
        this.contract.favoriteDealers = [...dealerIds];
    }
    get firstName() {
        return this.contract.firstName ?? '';
    }
    get fullName() {
        let fullName;
        if (this.firstName && this.lastName) {
            fullName = `${this.firstName} ${this.lastName}`;
        }
        else if (this.firstName) {
            fullName = this.firstName;
        }
        else if (this.lastName) {
            fullName = this.lastName;
        }
        else {
            fullName = this.name;
        }
        return fullName;
    }
    get lastName() {
        return this.contract.lastName ?? '';
    }
    get name() {
        return this.contract.name || '';
    }
    set name(name) {
        this.contract.name = name;
    }
    get nickname() {
        return this.contract.nickname ?? '';
    }
    set nickname(nickName) {
        this.contract.nickname = nickName;
    }
    get notifyEvents() {
        if (!this._notifyEvents) {
            const notifyEvents = this.contract.notifyEvents || {};
            this._notifyEvents = Object.freeze({
                appDisabled: notifyEvents.appDisabled || false,
                email: notifyEvents.email || false,
                text: notifyEvents.text || false,
            });
        }
        return this._notifyEvents;
    }
    set notifyEvents(profileNotificationPreference) {
        this.contract.notifyEvents = { ...profileNotificationPreference };
        this._notifyEvents = null;
    }
    get notifyRecommendedMaintenance() {
        if (!this._notifyRecommendedMaintenance) {
            const notifyRecommendedMaintenance = this.contract.notifyRecommendedMaintenance || {};
            this._notifyRecommendedMaintenance = Object.freeze({
                appDisabled: notifyRecommendedMaintenance.appDisabled || false,
                email: notifyRecommendedMaintenance.email || false,
                text: notifyRecommendedMaintenance.text || false,
            });
        }
        return this._notifyRecommendedMaintenance;
    }
    set notifyRecommendedMaintenance(profileNotificationPreference) {
        this.contract.notifyRecommendedMaintenance = {
            ...profileNotificationPreference,
        };
        this._notifyRecommendedMaintenance = null;
    }
    get notifyServiceBulletin() {
        if (!this._notifyServiceBulletin) {
            const notifyServiceBulletin = this.contract.notifyServiceBulletin || {};
            this._notifyServiceBulletin = Object.freeze({
                appDisabled: notifyServiceBulletin.appDisabled || false,
                email: notifyServiceBulletin.email || false,
                text: notifyServiceBulletin.text || false,
            });
        }
        return this._notifyServiceBulletin;
    }
    set notifyServiceBulletin(profileNotificationPreference) {
        this.contract.notifyServiceBulletin = {
            ...profileNotificationPreference,
        };
        this._notifyServiceBulletin = null;
    }
    get phoneNumber() {
        return this.contract.phoneNumber ?? '';
    }
    set phoneNumber(phoneNumber) {
        this.contract.phoneNumber = phoneNumber;
    }
    get postalCode() {
        return this.contract.postalCode || '';
    }
    set postalCode(postalCode) {
        this.contract.postalCode = postalCode;
    }
    get preferredCulture() {
        return this.contract.preferredCulture ?? '';
    }
    set preferredCulture(cultureId) {
        this.contract.preferredCulture = cultureId;
    }
    get riderColor() {
        return this.contract.riderColor ?? 'Blue';
    }
    set riderColor(riderColor) {
        this.contract.riderColor = riderColor;
    }
    get isPhoneNumberShared() {
        return this.contract.isPhoneNumberShared ?? false;
    }
    set isPhoneNumberShared(isPhoneNumberShared) {
        this.contract.isPhoneNumberShared = isPhoneNumberShared;
    }
    get riderId() {
        return this.contract.riderId || '';
    }
    get roles() {
        return (this.contract.roles ?? []);
    }
    get settings() {
        if (!this._settings) {
            const profileSettings = (this.contract.settings || []).map((settings) => {
                return Object.freeze({
                    appType: settings.appType,
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    demoContent: Object.freeze({ ...settings.demoContent }),
                    notifications: settings.notifications,
                    promotionalContent: Object.freeze({
                        ...settings.promotionalContent,
                    }),
                    syncContent: Object.freeze({ ...settings.syncContent }),
                });
            });
            this._settings = Object.freeze(profileSettings);
        }
        return this._settings;
    }
    set settings(settings) {
        this.contract.settings = settings;
        this._settings = null;
    }
    /**
     * @description User's preferred system of measurement. Unset values are significant, do not default to 'Standard'.
     */
    get systemOfMeasurement() {
        return this.contract.systemOfMeasurement ?? 'Standard';
    }
    set systemOfMeasurement(systemOfMeasurement) {
        this.contract.systemOfMeasurement = systemOfMeasurement;
    }
    clone() {
        return new UserProfile(cloneDeep(this.contract));
    }
    toContract() {
        return cloneDeep(this.contract);
    }
}

class UserProfileResourceService {
    constructor(config, http) {
        this.config = config;
        this.http = http;
    }
    getUserProfile() {
        const params = new HttpParams().append('skipRiderGroups', 'true');
        return this.http.get(`${this.config.apiUrl.rideCommand}/profile`, { params });
    }
    saveUserProfile(contract) {
        return this.http.put(`${this.config.apiUrl.rideCommand}/profile`, contract);
    }
    updatePromoContent(promotionalContent) {
        return this.http.put(`${this.config.apiUrl.rideCommand}/profile/content/promo`, promotionalContent);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UserProfileResourceService, deps: [{ token: i1.ConfigService }, { token: i2.WebComponentHttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UserProfileResourceService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UserProfileResourceService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1.ConfigService }, { type: i2.WebComponentHttpClient }] });

class UserProfileService {
    constructor(config, resource, authService, locale) {
        this.config = config;
        this.resource = resource;
        this.authService = authService;
        this.locale = locale;
        this.userProfileCache = new CacheCollectionObservable();
        // Services is missing all these for now
        this.accessTypes = [];
        this.alwaysNotifyTypes = [
            'HarmanDeviceInstalled',
            'HarmanDeviceRemoved',
            'SosAlert',
        ];
        this.maintenanceTypes = [
            'DiagnosticTroubleCode',
            'LowBattery',
            'MileageBasedMaintenance',
            'OtaUpdate',
            'PreHibernationAlert',
        ];
        this.ridesTypes = ['PostRideReport'];
        // Missing "Location Alerts Disabled"
        this.securityTypes = ['BumpAlert', 'Geofence', 'GeofenceBreach'];
        this.isMetricTrigger = new BehaviorSubject(this.locale.toLowerCase() !== 'en-us');
    }
    getUserProfile$(forceRefresh) {
        const cached = this.userProfileCache.getItem();
        if (!forceRefresh && cached) {
            return cached;
        }
        return this.userProfileCache.setItem(this.authService.isAuthenticated$().pipe(switchMap((isAuthenticated) => {
            if (!isAuthenticated) {
                return of(null);
            }
            return this.resource.getUserProfile().pipe(map((contract) => {
                const system = contract.systemOfMeasurement;
                this.isMetricTrigger.next(system ? system.toLowerCase() === 'metric' : this.locale.toLowerCase() !== 'en-us');
                return new UserProfile(contract);
            }));
        }), share()));
    }
    /**
     * @param forceRefresh
     * @deprecated - Use getUserProfile$
     */
    getUserProfile(forceRefresh) {
        if (!this.userProfile || forceRefresh) {
            this.userProfile = firstValueFrom(this.getUserProfile$());
        }
        return this.userProfile;
    }
    getIsMetric$() {
        return this.isMetricTrigger.asObservable();
    }
    /** @deprecated - Use getIsMetric$ */
    async getIsMetric() {
        return firstValueFrom(this.getIsMetric$());
    }
    getNotificationSettings$() {
        return this.getUserProfile$().pipe(map((userProfile) => {
            const existingSettings = userProfile?.settings.find((setting) => setting.appType === this.config.appSku);
            return existingSettings?.notifications ?? [];
        }), catchError(() => of([])));
    }
    /** @deprecated - Use getNotificationSettings$ */
    async getNotificationSettings() {
        return firstValueFrom(this.getNotificationSettings$());
    }
    getAccessNotificationPreferences$() {
        return this.getNotificationSettings$().pipe(map((notificationSettings) => this.getGroupedNotificationPreferences(notificationSettings, this.accessTypes)));
    }
    /** @deprecated - Use getAccessNotificationPreferences$ */
    async getAccessNotificationPreferences() {
        return firstValueFrom(this.getAccessNotificationPreferences$());
    }
    breakOutAccessPreferences(preference) {
        return this.accessTypes.map((type) => {
            return {
                preference,
                type,
            };
        });
    }
    getAlwaysNotifyPreferences$() {
        return this.getNotificationSettings$().pipe(map((notificationSettings) => this.getGroupedNotificationPreferences(notificationSettings, this.alwaysNotifyTypes)));
    }
    /** @deprecated - Use getAlwaysNotifyPreferences$ */
    async getAlwaysNotifyPreferences() {
        return firstValueFrom(this.getAlwaysNotifyPreferences$());
    }
    breakOutAlwaysNotifyPreferences(preference) {
        return this.alwaysNotifyTypes.map((type) => {
            return {
                preference,
                type,
            };
        });
    }
    getMaintenanceNotificationPreferences$() {
        return this.getNotificationSettings$().pipe(map((notificationSettings) => this.getGroupedNotificationPreferences(notificationSettings, this.maintenanceTypes)));
    }
    /** @deprecated - Use getMaintenanceNotificationsPreferences$ */
    async getMaintenanceNotificationPreferences() {
        return firstValueFrom(this.getMaintenanceNotificationPreferences$());
    }
    breakOutMaintenancePreferences(preference) {
        return this.maintenanceTypes.map((type) => {
            return {
                preference,
                type,
            };
        });
    }
    getRidesNotificationPreferences$() {
        return this.getNotificationSettings$().pipe(map((notificationSettings) => this.getGroupedNotificationPreferences(notificationSettings, this.ridesTypes)));
    }
    /** @deprecated - Use getRidesNotificationPreferences$ */
    async getRidesNotificationPreferences() {
        return firstValueFrom(this.getRidesNotificationPreferences$());
    }
    breakOutRidesPreferences(preference) {
        return this.ridesTypes.map((type) => {
            return {
                preference,
                type,
            };
        });
    }
    getSecurityNotificationPreferences$() {
        return this.getNotificationSettings$().pipe(map((notificationSettings) => this.getGroupedNotificationPreferences(notificationSettings, this.securityTypes)));
    }
    /** @deprecated - Use getSecurityNotificationsPreferences$ */
    async getSecurityNotificationPreferences() {
        return firstValueFrom(this.getSecurityNotificationPreferences$());
    }
    breakOutSecurityPreferences(preference) {
        return this.securityTypes.map((type) => {
            return {
                preference,
                type,
            };
        });
    }
    /**
     * Get the user's rider ID.
     * @returns An observable of the user's rider ID. This observable emits once and completes.
     */
    getUserRiderId$() {
        return this.getUserProfile$().pipe(map((profile) => profile?.riderId ?? ''));
    }
    /** @deprecated - Use getUserRiderId$ */
    async getUserRiderId() {
        return firstValueFrom(this.getUserRiderId$());
    }
    // This is to be used only for functionality that is visible to developers.
    getUserRoles$() {
        return this.getUserProfile$().pipe(map((userProfile) => userProfile?.roles ?? []));
    }
    isClientAdmin$() {
        return this.getUserRoles$().pipe(map((userRoles) => userRoles.some((userRole) => userRole === 'ROLE_SUPER_USER' || userRole === 'ROLE_CUSTOMER_SUPPORT')));
    }
    /** @deprecated - Use isClientAdmin$ */
    async isClientAdmin() {
        return firstValueFrom(this.isClientAdmin$());
    }
    isDevUser$() {
        return this.getUserRoles$().pipe(map((userRoles) => userRoles.some((userRole) => userRole === 'ROLE_DEV')));
    }
    /** @deprecated - Use isDevUser$ */
    async isDevUser() {
        return firstValueFrom(this.isDevUser$());
    }
    isGisUser$() {
        return this.getUserRoles$().pipe(map((userRoles) => userRoles.some((userRole) => userRole === 'ROLE_GIS')));
    }
    /** @deprecated - Use isGisUser$ */
    async isGisUser() {
        return firstValueFrom(this.isGisUser$());
    }
    isOwner$(riderId) {
        return this.getUserRiderId$().pipe(map((userRiderId) => userRiderId === riderId));
    }
    /**
     * @param riderId
     * @deprecated - Use isOwner$
     */
    async isOwner(riderId) {
        return firstValueFrom(this.isOwner$(riderId));
    }
    isEulaAccepted$(eulaVersion) {
        let currentAcceptedEula = '';
        return this.getUserProfile$().pipe(map((userProfile) => {
            const eula = userProfile?.acceptedEulas.find((acceptedEula) => acceptedEula.type === this.config.appSku);
            if (!eula && userProfile?.eulaVersion && this.config.appSku === 'ORV') {
                // check old location for accepted eula version in case user has not logged in recently
                currentAcceptedEula = userProfile.eulaVersion;
            }
            else {
                currentAcceptedEula = eula?.version ?? '';
            }
            return currentAcceptedEula === eulaVersion;
        }));
    }
    /**
     * @param eulaVersion
     * @deprecated - Use isEulaAccepted$
     */
    async isEulaAccepted(eulaVersion) {
        return firstValueFrom(this.isEulaAccepted$(eulaVersion));
    }
    setCurrentRideType$(currentRideType) {
        const updatedProfile = this.userProfileCache.setItem(this.getUserProfile$().pipe(switchMap((userProfile) => {
            if (!userProfile) {
                return of(null);
            }
            userProfile.currentRideType = currentRideType;
            return this.updateUserProfile$(userProfile);
        })));
        this.userProfile = firstValueFrom(updatedProfile);
        return updatedProfile;
    }
    /**
     * @param currentRideType
     * @deprecated - Use setCurrentRideType$
     */
    async setCurrentRideType(currentRideType) {
        return firstValueFrom(this.setCurrentRideType$(currentRideType));
    }
    updateNotificationSettings$(profile, notifications) {
        const contract = profile.toContract();
        const settingsIndex = contract.settings?.findIndex((setting) => setting.appType === this.config.appSku);
        if (contract.settings && typeof settingsIndex !== 'undefined' && settingsIndex >= 0) {
            contract.settings[settingsIndex].notifications = notifications;
        }
        const updatedProfile = this.userProfileCache.setItem(this.resource.saveUserProfile(contract).pipe(map(() => {
            // returned contract is missing riderId, using contract passed in
            return new UserProfile(contract);
        })));
        this.userProfile = firstValueFrom(updatedProfile);
        return updatedProfile;
    }
    updatePromoContent$(promoContent) {
        const updatedProfile = this.userProfileCache.setItem(this.resource.updatePromoContent(promoContent).pipe(map((contract) => new UserProfile(contract))));
        this.userProfile = firstValueFrom(updatedProfile);
        return updatedProfile;
    }
    /**
     * @param promoContent
     * @deprecated - Use updatePromoContent$
     */
    async updatePromoContent(promoContent) {
        return firstValueFrom(this.updatePromoContent$(promoContent));
    }
    updateUserProfile$(profile) {
        const contract = profile.toContract();
        const updatedProfile = this.userProfileCache.setItem(this.resource.saveUserProfile(contract).pipe(map(() => new UserProfile(contract)), tap(() => {
            this.isMetricTrigger.next(contract.systemOfMeasurement
                ? contract.systemOfMeasurement.toLowerCase() === 'metric'
                : this.locale.toLowerCase() !== 'en-us');
        })));
        this.userProfile = firstValueFrom(updatedProfile);
        return updatedProfile;
    }
    /**
     * @param profile
     * @deprecated - Use updateUserProfile$
     */
    async updateUserProfile(profile) {
        return firstValueFrom(this.updateUserProfile$(profile));
    }
    acceptEula$(eulaVersion) {
        return this.getUserProfile$().pipe(switchMap((userProfile) => {
            if (!userProfile) {
                return of(null);
            }
            const acceptedEula = { type: this.config.appSku, version: eulaVersion };
            userProfile = this.updateEulas(userProfile, acceptedEula);
            const contract = userProfile.toContract();
            const updatedProfile = this.userProfileCache.setItem(this.resource.saveUserProfile(contract).pipe(map(() => new UserProfile(contract))));
            this.userProfile = firstValueFrom(updatedProfile);
            return updatedProfile;
        }));
    }
    /**
     * @param eulaVersion
     * @deprecated - Use acceptEula$
     */
    async acceptEula(eulaVersion) {
        return firstValueFrom(this.acceptEula$(eulaVersion));
    }
    getGroupedNotificationPreferences(notificationSettings, notificationTypes) {
        if (notificationSettings.length === 0) {
            return {
                appDisabled: false,
                email: true,
                text: true,
            };
        }
        const initialPreference = {
            appDisabled: false,
            email: false,
            text: false,
        };
        const preferences = notificationSettings.reduce((accumulator, setting) => {
            let { groupedPreference, hasAnySavedSettings } = accumulator;
            if (setting.type && notificationTypes.includes(setting.type)) {
                groupedPreference = {
                    appDisabled: groupedPreference.appDisabled || setting.preference?.appDisabled,
                    email: groupedPreference.email || setting.preference?.email,
                    text: groupedPreference.text || setting.preference?.text,
                };
                hasAnySavedSettings = true;
            }
            return { groupedPreference, hasAnySavedSettings };
        }, { groupedPreference: initialPreference, hasAnySavedSettings: false });
        // return default preferences if user hasn't set anything
        if (!preferences.hasAnySavedSettings) {
            return {
                appDisabled: false,
                email: true,
                text: true,
            };
        }
        return preferences.groupedPreference;
    }
    updateEulas(profile, acceptedEula) {
        const eulasWithoutPreviousAccepted = profile.acceptedEulas.filter((eula) => eula.type !== this.config.appSku);
        const acceptedEulas = [...eulasWithoutPreviousAccepted, acceptedEula];
        profile.acceptedEulas = acceptedEulas;
        return profile;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UserProfileService, deps: [{ token: i1.ConfigService }, { token: UserProfileResourceService }, { token: i3.AuthService }, { token: LOCALE_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UserProfileService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UserProfileService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1.ConfigService }, { type: UserProfileResourceService }, { type: i3.AuthService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [LOCALE_ID]
                }] }] });

/**
 * Generated bundle index. Do not edit.
 */

export { UserProfile, UserProfileResourceService, UserProfileService };
